import { useQueryState } from 'nuqs';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader } from '@reflex/ui/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { LegalConsentLine } from '@/components/auth/LegalConsentLine';

interface AuthorizeDetails {
  /** Friendly name of the MCP client requesting access, when known. */
  clientName: string | null;
  provider: 'github' | 'google';
}

interface AuthorizeErrorBody {
  error?: string;
  error_description?: string;
}

async function fetchAuthorizeDetails(pending: string): Promise<AuthorizeDetails> {
  const res = await fetch(`/api/oauth/authorize/pending?pending=${encodeURIComponent(pending)}`, {
    headers: { Accept: 'application/json' },
  });
  const body = (await res.json().catch(() => ({}))) as {
    client_name?: string | null;
    provider?: 'github' | 'google';
  } & AuthorizeErrorBody;
  if (!res.ok) {
    throw new Error(
      body.error_description ?? body.error ?? `Failed to load this request (${res.status})`,
    );
  }
  return { clientName: body.client_name ?? null, provider: body.provider ?? 'github' };
}

const PROVIDER_LABEL: Record<AuthorizeDetails['provider'], string> = {
  github: 'GitHub',
  google: 'Google',
};

/**
 * Public approval screen for the MCP OAuth handshake.
 *
 * `GET /api/oauth/authorize` used to 302 straight to GitHub or Google, which
 * meant Reflex rendered nothing at all before an account was created and its
 * `legal_accepted_at` stamped. That made the stamp a record of policies the
 * user had never been shown. This page is the surface that makes it true, and
 * it is also the resource-owner approval step RFC 6749 §4.1 expects — the
 * endpoint previously auto-approved every registered client silently.
 *
 * Not gated by `AuthGate`: the visitor has no session yet (that is the point of
 * the flow), and the unguessable single-use `pending` id is the only authority
 * the server needs. Continue is a full-page navigation to
 * `/api/oauth/authorize/continue`, which carries the same id to the provider.
 */
export function AuthorizePage() {
  const [pending] = useQueryState('pending');

  const query = useQuery({
    queryKey: ['oauth-authorize-pending', pending],
    queryFn: () => fetchAuthorizeDetails(pending ?? ''),
    enabled: !!pending,
    retry: false,
  });

  const clientName = query.data?.clientName ?? null;
  const clientLabel = clientName ?? 'An MCP client';
  const providerLabel = query.data ? PROVIDER_LABEL[query.data.provider] : null;
  const loadError = query.error instanceof Error ? query.error.message : null;
  const showLoading = !!pending && query.isLoading;
  const canContinue = !!pending && !!query.data && !loadError;

  return (
    <div
      className="flex min-h-dvh items-center justify-center bg-background p-4"
      data-testid="oauth-authorize-page"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <PanelTitle>Connect to Reflex</PanelTitle>
          <CardDescription>
            {clientName
              ? `${clientName} wants to connect to your Reflex account.`
              : 'An MCP client (Claude, Cursor, …) wants to connect to your Reflex account.'}{' '}
            {providerLabel
              ? `Continue to sign in with ${providerLabel}.`
              : 'Continue to sign in with your identity provider.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {!pending && (
            <Alert variant="destructive" data-testid="oauth-authorize-missing">
              <AlertCircle className="size-4" />
              <AlertTitle>Missing authorization request</AlertTitle>
              <AlertDescription>
                This link is missing its request id. Restart the connection from your MCP client to
                try again.
              </AlertDescription>
            </Alert>
          )}

          {showLoading && (
            <p className="text-center text-sm text-muted-foreground">Loading request...</p>
          )}

          {pending && loadError && (
            <Alert variant="destructive" data-testid="oauth-authorize-load-error">
              <AlertCircle className="size-4" />
              <AlertTitle>Authorization request expired</AlertTitle>
              <AlertDescription>
                {loadError} Return to your MCP client (Claude, Cursor, …) and restart the connection
                to try again.
              </AlertDescription>
            </Alert>
          )}

          {canContinue && (
            <>
              <p className="text-sm text-muted-foreground" data-testid="oauth-authorize-scope">
                {clientLabel} will be able to read and act on the Reflex organization you choose
                next, on your behalf. You can revoke access at any time from your account settings.
              </p>
              <Button
                className="w-full"
                data-testid="oauth-authorize-continue"
                onClick={() => {
                  // Full-page navigation, not a router push: the next hop is
                  // the API server's redirect out to GitHub or Google.
                  window.location.assign(
                    `/api/oauth/authorize/continue?pending=${encodeURIComponent(pending)}`,
                  );
                }}
              >
                Continue with {providerLabel}
              </Button>
              {/* The record this makes honest — see `stampSignIn`. */}
              <LegalConsentLine
                lead="By authorizing you agree to the"
                data-testid="oauth-authorize-legal-consent"
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
